'use client';

import { c as _c } from "react/compiler-runtime";
import type { SelectedSnippet, SelectionRoot } from '@/components/highlighted-text-popup/utils';
import type { ReactNode } from 'react';
import { useCallback, useEffect } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { toast } from 'sonner';
import { isPrV2HotkeyTextEntryTarget, isPrV2SidebarShortcutConflictTarget } from '../components/diff/hooks/use-pr-v2-hotkey-interactive-target';
import { useZenModeToggle } from '../hooks/use-zen-mode-toggle';
import { HighlightedTextPopup } from '@/components/highlighted-text-popup/highlighted-text-popup';
import { formatSelectedSnippetForChat, getActiveSelection, getSelectedSnippet } from '@/components/highlighted-text-popup/utils';
import { KeyboardKey } from '@/components/ui/keyboard-key';
import { useSidebar } from '@/components/ui/sidebar';
import { removeMetadataFromComment } from '@/lib/ai/code-review-service/utils/metadata';
import { clientLogger } from '@/lib/client-logger';
import { safeLocalStorage } from '@/lib/safe-storage';
const PR_V2_SHORTCUT_TOAST_KEY = 'pr-v2-shortcuts-tip-shown';
const PR_V2_SHORTCUT_TOAST_DURATION_MS = 7000;
const PR_V2_DIFF_SHADOW_HOST_SELECTOR = 'diffs-container';
const PR_V2_SELECTION_BLOCKED_SELECTOR = '[data-sidebar="sidebar"], [data-highlighted-text-popup="true"]';

/**
 * Returns the DOM roots that can own text selections on the PR review page.
 *
 * Pierre renders diff code in open shadow roots, so code selections may not be
 * observable through the document selection alone.
 */
function getPrV2SelectionRoots(): SelectionRoot[] {
  const diffShadowRoots = Array.from(document.querySelectorAll<HTMLElement>(PR_V2_DIFF_SHADOW_HOST_SELECTOR), host => host.shadowRoot).filter((shadowRoot): shadowRoot is ShadowRoot => Boolean(shadowRoot));
  return [document, ...diffShadowRoots];
}

/**
 * Keeps the highlighted-text popup out of text-entry and sidebar surfaces while
 * still allowing selections inside PR descriptions, comments, and diff code.
 */
function isPrV2HighlightedTextSelectionAllowed(container: Element): boolean {
  if (container.closest(PR_V2_SELECTION_BLOCKED_SELECTOR)) {
    return false;
  }
  return !isPrV2HotkeyTextEntryTarget(container);
}
export function PrV2KeyboardShortcutsProvider(t0) {
  const $ = _c(23);
  const {
    children
  } = t0;
  const {
    toggleSidebar
  } = useSidebar();
  const {
    toggle: toggleZenMode
  } = useZenModeToggle();
  let t1;
  if ($[0] !== toggleSidebar) {
    t1 = snippet => {
      toggleSidebar(formatSelectedSnippetForChat({
        ...snippet,
        text: removeMetadataFromComment(snippet.text)
      }));
    };
    $[0] = toggleSidebar;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleSelectedTextChatAction = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useEffect(_temp, t2);
  let t3;
  if ($[3] !== handleSelectedTextChatAction || $[4] !== toggleSidebar) {
    t3 = event => {
      if (event.defaultPrevented || isPrV2SidebarShortcutConflictTarget(event.target)) {
        return;
      }
      event.preventDefault();
      getSelectedSnippet(getActiveSelection(getPrV2SelectionRoots())).then(snippet_0 => {
        if (snippet_0) {
          handleSelectedTextChatAction(snippet_0);
          return;
        }
        toggleSidebar();
      }).catch(_temp2);
    };
    $[3] = handleSelectedTextChatAction;
    $[4] = toggleSidebar;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      enableOnFormTags: true,
      enabled: true
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== handleSelectedTextChatAction || $[8] !== toggleSidebar) {
    t5 = [handleSelectedTextChatAction, toggleSidebar];
    $[7] = handleSelectedTextChatAction;
    $[8] = toggleSidebar;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  useHotkeys("mod+i", t3, t4, t5);
  let t6;
  if ($[10] !== toggleSidebar) {
    t6 = event_0 => {
      if (isPrV2SidebarShortcutConflictTarget(event_0.target)) {
        return;
      }
      event_0.preventDefault();
      toggleSidebar();
    };
    $[10] = toggleSidebar;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      enableOnFormTags: true
    };
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== toggleSidebar) {
    t8 = [toggleSidebar];
    $[13] = toggleSidebar;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  useHotkeys("mod+b", t6, t7, t8);
  let t9;
  if ($[15] !== toggleZenMode) {
    t9 = event_1 => {
      event_1.preventDefault();
      toggleZenMode();
    };
    $[15] = toggleZenMode;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      enableOnFormTags: true,
      enabled: true
    };
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  useHotkeys("mod+backslash", t9, t10);
  let t11;
  if ($[18] !== handleSelectedTextChatAction) {
    t11 = <HighlightedTextPopup getSelectionRoots={getPrV2SelectionRoots} isSelectionAllowed={isPrV2HighlightedTextSelectionAllowed} onChatClickAction={handleSelectedTextChatAction} />;
    $[18] = handleSelectedTextChatAction;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== children || $[21] !== t11) {
    t12 = <>{children}{t11}</>;
    $[20] = children;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  return t12;
}
function _temp2(error) {
  clientLogger.error("Failed to read the text selection for \u2318I", error);
}
function _temp() {
  if (window.localStorage.getItem(PR_V2_SHORTCUT_TOAST_KEY) === "1") {
    return;
  }
  toast.info(<span>Tip: use <KeyboardKey>J</KeyboardKey> and <KeyboardKey>K</KeyboardKey> to move between files.</span>, {
    duration: PR_V2_SHORTCUT_TOAST_DURATION_MS
  });
  safeLocalStorage.setItem(PR_V2_SHORTCUT_TOAST_KEY, "1");
}